/**
 * Agent runs API client.
 *
 * `GET /agent-runs` is already owner-scoped server-side (see
 * `router.list_runs` / `RunService.list_runs`) — this must not filter the
 * result again client-side.
 *
 * `GET /agent-runs/{id}` 404s for a run that does not exist AND for one
 * the viewer may not see: it shows the viewer's own runs, every run to an
 * admin, and a run to a reader (Reporter and up) of its project
 * (`run_visibility.can_view_run`). `POST /agent-runs/{id}/stop` 404s for
 * any run but the viewer's own (an admin's: any), so a reader who can see a
 * run still can't stop it; the detail's `can_stop` says which. Callers of
 * `getRun`/`stopRun` must not turn that 404 into UI copy implying the run
 * exists but is merely inaccessible.
 */

import { api } from '@services/api';
import { API_PATHS } from '@utils/constants';
import {
  AgentRunDetailSchema,
  RunListResponseSchema,
} from '../schemas/agentRun.schemas';
import type { AgentRun, AgentRunDetail } from '../types/agentRun.types';

/** The caller's runs, newest first; `projectId` narrows them to one project (server-side). */
export async function listRuns(projectId: string | null = null): Promise<AgentRun[]> {
  const response = projectId === null
    ? await api.get(API_PATHS.AGENT_RUNS)
    : await api.get(API_PATHS.AGENT_RUNS, { params: { project_id: projectId } });
  return RunListResponseSchema.parse(response.data).runs;
}

export async function getRun(id: string): Promise<AgentRunDetail> {
  const response = await api.get(`${API_PATHS.AGENT_RUNS}/${id}`);
  return AgentRunDetailSchema.parse(response.data);
}

export async function stopRun(id: string): Promise<void> {
  await api.post(`${API_PATHS.AGENT_RUNS}/${id}/stop`);
}
